import { useRef, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { extractTweetId } from 'utils/extractTweetId'

const ppGreen = require('styles/variables')['--pp-green']

export interface TweetOptions {
  cards?: 'hidden'
  conversation?: 'none'
  theme?: 'dark' | 'light'
  linkColor?: string
  width?: number
  align?: 'left' | 'right' | 'center',
  dnt?: boolean
}

const DEFAULT_TWEET_WIDTH = 520 // 520 is the max allowed value by Twitter

export interface EmbeddedTweetProps {
  url: string
  options?: TweetOptions
  onLoaded?: () => void
  onFailed?: () => void
}

const defaultOptions: TweetOptions = {
  width: DEFAULT_TWEET_WIDTH,
  align: 'center',
  conversation: 'none',
  linkColor: ppGreen,
  dnt: true
}

export function EmbeddedTweet(props: EmbeddedTweetProps) {
  const errorElement = <p><FormattedMessage id="errors.generic" /></p>
  const elementRef = useRef(null)
  const tweetId = extractTweetId(props.url)
  // EXPL: keep latest callbacks/options in refs so the embed only re-runs when the tweet URL
  // changes — callers may pass non-memoized callbacks/options that should not re-trigger the embed.
  const onLoadedRef = useRef(props.onLoaded)
  const onFailedRef = useRef(props.onFailed)
  const optionsRef = useRef(props.options)
  useEffect(() => {
    onLoadedRef.current = props.onLoaded
    onFailedRef.current = props.onFailed
    optionsRef.current = props.options
  })

  useEffect(() => {
    const twitterWidgets = (window as any).twttr?.widgets
    if (!twitterWidgets) {
      return
    }
    twitterWidgets.createTweet(tweetId, elementRef.current, optionsRef.current || defaultOptions)
      .then(() => onLoadedRef.current?.())
      .catch(() => onFailedRef.current?.())
  }, [props.url, tweetId])

  if (!props.url) {
    console.error('[EmbeddedTweet] missing tweet url')
    return errorElement
  }

  if (!(window as any).twttr || !tweetId) {
    console.error('[EmbeddedTweet] twitter sdk not loaded or tweet url is invalid')
    return errorElement
  }

  return (
    <blockquote ref={elementRef}></blockquote>
  )
}

export default EmbeddedTweet
